"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { useState } from "react";
import type { ProductCardD2CProps } from "../../blocks/product-card-d2c/product-card-d2c";
import { Button } from "../../elements/button/button";
import { TextD2C } from "../../elements/text-d2c/text";
import { textD2CSizes } from "../../elements/text-d2c/text-d2c.constants";
import { IconPlus } from "../../icons/plus-icon";
import { Container, type ContainerProps } from "../../layout/grid";
import type { BlockInspectorProps } from "../../lib/inspector";
import { cn } from "../../lib/utils";
import { Carousel } from "../../patterns/carousel/carousel";
import {
  ProductCardD2CClient,
  type ProductCardD2CClientProps,
} from "../product-card-d2c/product-card-d2c.client";
import { useInsideTabContainer } from "../tabs/tab-container-context";
import {
  type ActiveProductFilterD2C,
  type ProductFilterGroupD2C,
  ProductListingActiveFiltersD2C,
  type ProductListingControlsMicrocopies,
  ProductListingFiltersD2C,
  ProductListingMobileFiltersD2C,
  ProductListingToolbarD2C,
  type ProductSortOptionD2C,
} from "./product-listing-controls-d2c";
import {
  PRODUCT_LISTING_SKELETON_COUNT,
  ProductListingCarouselSkeleton,
  ProductListingGridSkeleton,
  ProductListingGridSkeletonItems,
} from "./product-listing-d2c.skeleton";

export type ProductListingD2CVariant = "grid" | "carousel";
export type ProductListingPaginationModeD2C = "paginate" | "showAll";

export type ProductListingPaginationD2C = {
  currentPage: number;
  totalPages: number;
};

export interface ProductListingD2CMicrocopies
  extends ProductListingControlsMicrocopies {
  previousSlide: string;
  nextSlide: string;
  emptyState: string;
  loadMore: string;
  loadingProducts: string;
  unableToLoadProducts: string;
  retry: string;
}

const productListingVariants = cva("", {
  variants: {
    backgroundColor: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
  },
});

export interface ProductListingD2CProps
  extends VariantProps<typeof productListingVariants> {
  sysId?: string;
  variant: ProductListingD2CVariant;
  products: ProductCardD2CClientProps[];
  title?: string;
  titleHeadingLevel?: "h2" | "h3";
  anchorId?: string;
  count?: number;
  showFilters?: boolean;
  showSort?: boolean;
  filterGroups?: ProductFilterGroupD2C[];
  activeFilters?: ActiveProductFilterD2C[];
  sortOptions?: ProductSortOptionD2C[];
  selectedSort?: string;
  paginationMode?: ProductListingPaginationModeD2C;
  pagination?: ProductListingPaginationD2C;
  defaultFiltersOpen?: boolean;
  showCarouselNavigationArrows?: boolean;
  onFilter?: () => void;
  onSort?: () => void;
  onFilterChange?: (query: string) => void;
  onClearFilters?: () => void;
  onSortChange?: (code: string) => void;
  onRangeFilterChange?: (code: string, range: [number, number]) => void;
  onLoadMore?: () => void;
  onRetry?: () => void;
  loading?: boolean;
  loadingMore?: boolean;
  error?: boolean;
  className?: string;
  fullWidth?: ContainerProps["fullWidth"];
  backgroundColor?: "black" | "white" | "whitesmoke";
  paddingTop?: ContainerProps["paddingTop"];
  paddingBottom?: ContainerProps["paddingBottom"];
  microcopies: ProductListingD2CMicrocopies;
  inspectorProps?: BlockInspectorProps;
}

function ProductListingHeader(
  props: Readonly<{
    title?: string;
    titleHeadingLevel?: "h2" | "h3";
    anchorId?: string;
    count?: number;
    showFilters?: boolean;
    showSort?: boolean;
    filtersOpen: boolean;
    mobileFiltersOpen: boolean;
    onFiltersToggle: () => void;
    onMobileFiltersOpen: () => void;
    onSortChange?: (code: string) => void;
    selectedSort?: string;
    sortOptions: ProductSortOptionD2C[];
    loading?: boolean;
    isDarkBackground: boolean;
    microcopies: ProductListingD2CMicrocopies;
  }>
) {
  const hasTitle = !!props.title;
  const hasActions = !!props.showFilters || !!props.showSort;
  const TitleHeading = props.titleHeadingLevel ?? "h2";

  if (!(hasTitle || hasActions)) {
    return null;
  }

  return (
    <div
      className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center"
      data-testid="product-listing-header"
      id={props.anchorId}
    >
      {hasTitle ? (
        <TitleHeading
          className={cn(
            "font-bold font-brand",
            textD2CSizes.h3,
            props.isDarkBackground ? "text-white" : "text-black"
          )}
          data-testid="product-listing-title"
        >
          {props.title}
          {props.count === undefined ? null : (
            <span className="ml-2 font-normal">({props.count})</span>
          )}
        </TitleHeading>
      ) : (
        <span />
      )}

      {hasActions ? (
        <div
          className="flex w-full items-center gap-2 lg:w-auto"
          data-testid="product-listing-actions"
        >
          <ProductListingToolbarD2C
            disabled={props.loading}
            filtersOpen={props.filtersOpen}
            microcopies={props.microcopies}
            mobileFiltersOpen={props.mobileFiltersOpen}
            onFiltersToggle={props.onFiltersToggle}
            onMobileFiltersOpen={props.onMobileFiltersOpen}
            onSortChange={props.onSortChange}
            selectedSort={props.selectedSort}
            showFilters={props.showFilters}
            showSort={props.showSort}
            sortOptions={props.sortOptions}
          />
        </div>
      ) : null}
    </div>
  );
}

function getProductKey(
  product: ProductCardD2CProps,
  index: number,
  sysId?: string
) {
  return `${sysId ?? ""}-${product.microcopies.name}-${index}`;
}

function GridProductListing(
  props: Readonly<
    Pick<ProductListingD2CProps, "sysId" | "products"> & {
      /** Placeholder cards appended while the next page loads. */
      pendingCount?: number;
    }
  >
) {
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
      data-testid="product-listing-grid"
    >
      {props.products.map((product, index) => (
        <div
          data-testid="product-listing-grid-item"
          key={getProductKey(product, index, props.sysId)}
        >
          <ProductCardD2CClient {...product} />
        </div>
      ))}
      {props.pendingCount ? (
        <ProductListingGridSkeletonItems
          count={props.pendingCount}
          keyPrefix="pending"
        />
      ) : null}
    </div>
  );
}

function getRefreshSkeletonCount(productCount: number) {
  return Math.min(
    productCount || PRODUCT_LISTING_SKELETON_COUNT,
    PRODUCT_LISTING_SKELETON_COUNT
  );
}

function CarouselProductListing(
  props: Readonly<
    Pick<
      ProductListingD2CProps,
      | "sysId"
      | "products"
      | "microcopies"
      | "backgroundColor"
      | "showCarouselNavigationArrows"
    >
  >
) {
  const isDarkBackground = props.backgroundColor === "black";

  return (
    <div
      className="px-0 pl-3 md:pl-6 lg:pl-8 xl:pl-11.5"
      data-testid="product-listing-carousel"
    >
      <Carousel
        disableDragWhenNotScrollable
        dragFree
        nextSlideText={props.microcopies.nextSlide}
        previousSlideText={props.microcopies.previousSlide}
        progressIndicatorClassName={cn(isDarkBackground && "bg-white")}
        progressTrackClassName={cn(isDarkBackground && "bg-white/30")}
        showNavigationArrows={props.showCarouselNavigationArrows}
        showProgressBar
        wheelGestures
      >
        {props.products.map((product, index) => (
          <div
            className="mx-2 min-w-0 shrink-0 grow-0 basis-[85vw] first:ml-0 last:mr-0 sm:basis-[320px] md:basis-[360px] lg:basis-[380px]"
            data-testid="product-listing-carousel-item"
            key={getProductKey(product, index, props.sysId)}
          >
            <ProductCardD2CClient {...product} />
          </div>
        ))}
      </Carousel>
    </div>
  );
}

function ProductListingLoadMore(
  props: Readonly<
    Pick<
      ProductListingD2CProps,
      | "loading"
      | "loadingMore"
      | "microcopies"
      | "onLoadMore"
      | "pagination"
      | "paginationMode"
    >
  >
) {
  const hasNextPage =
    props.paginationMode === "paginate" &&
    props.pagination !== undefined &&
    props.onLoadMore !== undefined &&
    props.pagination.currentPage + 1 < props.pagination.totalPages;

  if (!hasNextPage) {
    return null;
  }

  return (
    <div className="flex justify-center pt-20">
      <Button
        className="h-14 w-full max-w-[22.875rem] text-base"
        data-testid="product-listing-load-more"
        // A filter or sort refresh replaces the listing, so the next page
        // must wait for the new first page.
        disabled={props.loading}
        loading={props.loadingMore}
        onClick={props.onLoadMore}
        variant="outline"
      >
        {props.loadingMore ? null : (
          <IconPlus className="[&_svg]:size-full" size="sm" />
        )}
        {props.microcopies.loadMore}
      </Button>
    </div>
  );
}

export function ProductListingD2C(
  props: Readonly<ProductListingD2CProps>
): ReactNode {
  const [filtersOpen, setFiltersOpen] = useState(
    props.defaultFiltersOpen ?? false
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const fullWidth = props.fullWidth ?? true;
  const backgroundColor = props.backgroundColor ?? "white";
  const bgClasses = productListingVariants({ backgroundColor });
  const isDarkBackground = backgroundColor === "black";

  const isCarousel = props.variant === "carousel";
  const insideTabContainer = useInsideTabContainer();

  const listingContent = isCarousel ? (
    <CarouselProductListing
      backgroundColor={backgroundColor}
      microcopies={props.microcopies}
      products={props.products}
      showCarouselNavigationArrows={props.showCarouselNavigationArrows}
      sysId={props.sysId}
    />
  ) : (
    <GridProductListing
      pendingCount={
        props.loadingMore ? PRODUCT_LISTING_SKELETON_COUNT : undefined
      }
      products={props.products}
      sysId={props.sysId}
    />
  );

  const hideHeader = isCarousel && insideTabContainer;
  const header = hideHeader ? null : (
    <ProductListingHeader
      anchorId={props.anchorId}
      count={isCarousel ? undefined : props.count}
      filtersOpen={filtersOpen}
      isDarkBackground={isDarkBackground}
      loading={props.loading}
      microcopies={props.microcopies}
      mobileFiltersOpen={mobileFiltersOpen}
      onFiltersToggle={() => {
        setFiltersOpen((open) => !open);
        props.onFilter?.();
      }}
      onMobileFiltersOpen={() => {
        setMobileFiltersOpen(true);
        props.onFilter?.();
      }}
      onSortChange={(code) => {
        props.onSort?.();
        props.onSortChange?.(code);
      }}
      selectedSort={props.selectedSort}
      showFilters={props.showFilters}
      showSort={props.showSort}
      sortOptions={props.sortOptions ?? []}
      title={props.title}
      titleHeadingLevel={props.titleHeadingLevel}
    />
  );

  const filterPanel = props.showFilters ? (
    <ProductListingFiltersD2C
      activeFilters={props.activeFilters ?? []}
      disabled={props.loading}
      filterGroups={props.filterGroups ?? []}
      microcopies={props.microcopies}
      onClearAll={props.onClearFilters}
      onFilterChange={props.onFilterChange}
      onRangeChange={props.onRangeFilterChange}
    />
  ) : null;
  const mobileFilterDialog = props.showFilters ? (
    <ProductListingMobileFiltersD2C
      activeFilters={props.activeFilters ?? []}
      disabled={props.loading}
      filterGroups={props.filterGroups ?? []}
      microcopies={props.microcopies}
      onApply={() => setMobileFiltersOpen(false)}
      onClearAll={props.onClearFilters}
      onFilterChange={props.onFilterChange}
      onOpenChange={setMobileFiltersOpen}
      onRangeChange={props.onRangeFilterChange}
      open={mobileFiltersOpen}
    />
  ) : null;
  const activeFilterChips = props.showFilters ? (
    <ProductListingActiveFiltersD2C
      activeFilters={props.activeFilters ?? []}
      disabled={props.loading}
      microcopies={props.microcopies}
      onFilterChange={props.onFilterChange}
    />
  ) : null;
  const loadMore =
    props.error || props.products.length === 0 ? null : (
      <ProductListingLoadMore
        loading={props.loading}
        loadingMore={props.loadingMore}
        microcopies={props.microcopies}
        onLoadMore={props.onLoadMore}
        pagination={props.pagination}
        paginationMode={props.paginationMode}
      />
    );

  let content: ReactNode;

  if (props.error) {
    content = (
      <div
        className="flex min-h-56 flex-col items-center justify-center gap-4 py-10"
        data-testid="product-listing-error"
        role="alert"
      >
        <TextD2C style="no-mb">
          {props.microcopies.unableToLoadProducts}
        </TextD2C>
        {props.onRetry ? (
          <Button onClick={props.onRetry} variant="outline">
            {props.microcopies.retry}
          </Button>
        ) : null}
      </div>
    );
  } else if (props.loading) {
    // Filter and sort changes replace the products, so show placeholders
    // instead of the stale results.
    const count = getRefreshSkeletonCount(props.products.length);
    content = isCarousel ? (
      <ProductListingCarouselSkeleton count={count} />
    ) : (
      <ProductListingGridSkeleton count={count} />
    );
  } else if (props.products.length === 0) {
    content = (
      <div
        className="flex min-h-56 items-center justify-center py-10"
        data-testid="product-listing-empty"
      >
        <TextD2C style="no-mb">{props.microcopies.emptyState}</TextD2C>
      </div>
    );
  } else {
    content = listingContent;
  }

  return (
    <Container
      className={cn(
        "component-product-listing-d2c -mb-px overflow-x-clip",
        bgClasses,
        props.className
      )}
      fullWidth={fullWidth}
      outerContainerOnly={fullWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="component-product-listing-d2c"
      {...props.inspectorProps?.container}
    >
      <>
        <div className="px-3 md:px-6 lg:px-8 xl:px-11.5">
          {header}
          {activeFilterChips}
        </div>
        {mobileFilterDialog}
        {props.loading || props.loadingMore ? (
          <output className="sr-only">
            {props.microcopies.loadingProducts}
          </output>
        ) : null}
        {isCarousel ? (
          <>
            {filtersOpen ? (
              <div className="mb-6 px-3 md:px-6 lg:px-8 xl:px-11.5">
                {filterPanel}
              </div>
            ) : null}
            <div aria-busy={props.loading || props.loadingMore || undefined}>
              {content}
              {loadMore}
            </div>
          </>
        ) : (
          <div
            aria-busy={props.loading || props.loadingMore || undefined}
            className="px-3 md:px-6 lg:px-8 xl:px-11.5"
          >
            <div
              className={cn(
                props.showFilters &&
                  "grid items-start lg:grid-rows-1 lg:transition-[grid-template-columns] lg:duration-300 lg:ease-out",
                props.showFilters && filtersOpen
                  ? "lg:grid-cols-[20rem_minmax(0,1fr)]"
                  : "lg:grid-cols-[0_minmax(0,1fr)]"
              )}
              data-testid="product-listing-layout"
            >
              {props.showFilters ? (
                <aside
                  aria-hidden={!filtersOpen}
                  className="hidden min-h-0 min-w-0 overflow-visible lg:block"
                  inert={!filtersOpen}
                >
                  <div
                    className={cn(
                      "min-w-0 pb-6 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform lg:w-80 lg:pr-6 lg:pb-0",
                      filtersOpen
                        ? "translate-x-0"
                        : "-translate-x-[calc(100%+1rem)] md:-translate-x-[calc(100%+1.5rem)] lg:-translate-x-[calc(100%+2rem)] xl:-translate-x-[calc(100%+3rem)]"
                    )}
                  >
                    {filterPanel}
                  </div>
                </aside>
              ) : null}
              <div className="min-w-0">
                {content}
                {loadMore}
              </div>
            </div>
          </div>
        )}
      </>
    </Container>
  );
}
